import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, RefreshCw, ExternalLink, Download, CheckCircle, Circle, FileText, Search } from 'lucide-react'
import { useArticlesStore } from '@/lib/stores/articlesStore'
import { Button, Card, CardContent, Badge, Modal, Input, Spinner } from '@/components/ui'
import type { Article } from '@/types'

interface ImportResultState {
  total: number
  imported: number
  skipped: number
  errors: number
}

export function ArticlesPage() {
  const { articles, isLoading, fetchArticles, addArticle, importArticles, markArticleCompleted } = useArticlesStore()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isImportModalOpen, setIsImportModalOpen] = useState(false)
  const [newUrl, setNewUrl] = useState('')
  const [newType, setNewType] = useState('guida')
  const [isAdding, setIsAdding] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResultState | null>(null)

  useEffect(() => {
    fetchArticles()
  }, [fetchArticles])

  const handleAddArticle = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsAdding(true)
    const success = await addArticle(newUrl, newType)
    if (success) {
      setIsModalOpen(false)
      setNewUrl('')
    }
    setIsAdding(false)
  }

  const handleImport = async () => {
    setIsImporting(true)
    setImportResult(null)
    const result = await importArticles({ defaultType: 'guida' })
    if (result) {
      setImportResult(result)
    }
    setIsImporting(false)
  }

  const handleToggleCompleted = async (article: Article) => {
    await markArticleCompleted(article.id, !article.completed_at)
  }

  // Stats
  const completedCount = articles.filter(a => a.completed_at).length
  const withProposalsCount = articles.filter(a => (a.proposal_count || 0) > 0).length

  if (isLoading && articles.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Articoli</h1>
          <div className="flex gap-4 mt-1 text-sm">
            <span className="text-gray-500">{articles.length} totali</span>
            <span className="text-green-600">{completedCount} completati</span>
            <span className="text-blue-600">{withProposalsCount} con proposte</span>
            <span className="text-amber-600">{articles.length - completedCount} da fare</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => fetchArticles()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
          <Button variant="secondary" onClick={() => setIsImportModalOpen(true)}>
            <Download className="w-4 h-4 mr-2" />
            Importa da WP
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi
          </Button>
        </div>
      </div>

      {/* Articles Grid */}
      {articles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500 mb-4">Nessun articolo monitorato</p>
            <Button onClick={() => setIsModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi il primo articolo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Articolo
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Analisi
                  </th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {articles.map((article: Article) => (
                  <tr
                    key={article.id}
                    className={`hover:bg-gray-50 ${article.completed_at ? 'bg-green-50/50' : ''}`}
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleCompleted(article)}
                        className="flex items-center gap-2 group"
                        title={article.completed_at ? 'Segna come da fare' : 'Segna come completato'}
                      >
                        {article.completed_at ? (
                          <CheckCircle className="w-5 h-5 text-green-500 group-hover:text-green-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-300 group-hover:text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/articles/${article.id}`}
                        className={`font-medium hover:text-primary-600 ${article.completed_at ? 'text-gray-500 line-through' : 'text-gray-900'}`}
                      >
                        {article.title}
                      </Link>
                      <p className="text-sm text-gray-500 truncate max-w-md">
                        {article.url}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <Badge>{article.type}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {article.serp_keyword && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700" title="Keyword SERP">
                            <Search className="w-3 h-3" />
                            {article.serp_keyword}
                          </span>
                        )}
                        {(article.proposal_count || 0) > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700" title="Proposte AI">
                            <FileText className="w-3 h-3" />
                            {article.proposal_count}
                          </span>
                        )}
                        {!article.serp_keyword && (article.proposal_count || 0) === 0 && (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Add Article Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Aggiungi articolo"
      >
        <form onSubmit={handleAddArticle} className="space-y-4">
          <Input
            label="URL, ID o Slug"
            type="text"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="https://dokicasa.it/... oppure 66726 oppure tariffe-notaio"
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="guida">Guida</option>
              <option value="articolo">Articolo</option>
              <option value="landing">Landing Page</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Annulla
            </Button>
            <Button type="submit" isLoading={isAdding}>
              Aggiungi
            </Button>
          </div>
        </form>
      </Modal>

      {/* Import Modal */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false)
          setImportResult(null)
        }}
        title="Importa da WordPress"
      >
        <div className="space-y-4">
          {!importResult ? (
            <>
              <p className="text-sm text-gray-600">
                Importa automaticamente tutti gli articoli e le pagine pubblicate dal sito WordPress configurato.
              </p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> Gli articoli gia presenti nel database verranno saltati.
                  Le pagine saranno importate come "Landing Page", i post come "Guida".
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsImportModalOpen(false)}
                >
                  Annulla
                </Button>
                <Button onClick={handleImport} isLoading={isImporting}>
                  <Download className="w-4 h-4 mr-2" />
                  Avvia Import
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Import completato</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">Totale trovati</p>
                    <p className="text-2xl font-bold text-gray-900">{importResult.total}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-green-600">Importati</p>
                    <p className="text-2xl font-bold text-green-700">{importResult.imported}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">Gia presenti</p>
                    <p className="text-2xl font-bold text-gray-600">{importResult.skipped}</p>
                  </div>
                  {importResult.errors > 0 && (
                    <div className="p-3 bg-red-50 rounded-lg">
                      <p className="text-red-600">Errori</p>
                      <p className="text-2xl font-bold text-red-700">{importResult.errors}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => {
                    setIsImportModalOpen(false)
                    setImportResult(null)
                  }}
                >
                  Chiudi
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}
